<template>
  <vs-popup id="popupAiVoiceWrite" :title="$t('AI 음성 작성')" :active.sync="showPopup" :width="popupWidth">
    <div class="ai-voice">
      <!-- 상단: 진행률 -->
      <div class="progress-row">
        <div class="progress-head">
          <div>
            <span class="progress-percent">{{ progress.percent }}%</span>
            <span class="progress-count">{{ progress.filledCount }} / {{ progress.total }} {{ $t("항목") }}</span>
          </div>
          <span v-if="progress.unfilled.length === 0" class="progress-done">
            <feather-icon icon="CheckCircleIcon" svgClasses="w-4 h-4" />
            <span class="ml-1">{{ $t("필수 항목이 모두 작성되었습니다") }}</span>
          </span>
        </div>
        <div class="progress-bar-bg">
          <div class="progress-bar-fill" :style="{ width: progress.percent + '%' }"></div>
        </div>
      </div>

      <div class="body-grid">
        <!-- STEP 1 : 안내 -->
        <section class="panel panel-guide">
          <header class="panel-head">
            <span class="step-badge">1</span>
            <h6 class="panel-title">{{ $t("이렇게 말해보세요") }}</h6>
          </header>

          <!-- AI 가 만든 후속 질문. 반영 목록 아래에 두면 항목이 늘어날수록 밀려서
               정작 다음에 무슨 말을 해야 할지가 안 보인다 — 안내 단계 맨 위에 둔다. -->
          <div v-if="nextQuestions.length > 0" class="next-question-box">
            <div class="next-question-head">
              <feather-icon icon="HelpCircleIcon" svgClasses="w-4 h-4" />
              <span class="ml-1">{{ $t("다음 질문") }}</span>
            </div>
            <ol class="question-list">
              <li v-for="(question, index) in nextQuestions" :key="`question-${index}`">{{ question }}</li>
            </ol>
          </div>

          <ul class="example-list">
            <li v-for="(example, index) in schema.examples" :key="`example-${index}`">{{ example }}</li>
          </ul>

          <div class="panel-subhead">
            <span>{{ $t("아직 남은 항목") }}</span>
            <span class="count-badge">{{ progress.unfilled.length }}</span>
          </div>

          <p v-if="progress.unfilled.length === 0" class="empty-text success">{{ $t("모두 작성되었습니다.") }}</p>
          <div v-else class="chip-list">
            <span v-for="entry in visibleUnfilled" :key="`unfilled-${entry.id}`" class="chip">
              <em v-if="entry.group">{{ $t(entry.group) }}</em>{{ entry.label }}
            </span>
            <span v-if="progress.unfilled.length > unfilledLimit" class="chip chip-more">
              +{{ progress.unfilled.length - unfilledLimit }}
            </span>
          </div>
        </section>

        <!-- STEP 2 : 말하기 -->
        <section class="panel panel-record" :class="{ 'is-recording': isRecording }">
          <header class="panel-head">
            <span class="step-badge">2</span>
            <h6 class="panel-title">{{ $t("말하기") }}</h6>
          </header>

          <div class="record-body">
            <div class="record-status" :class="{ live: isRecording, busy: busy }">
              <span v-if="isRecording" class="rec-dot"></span>{{ statusText }}
            </div>

            <button
              type="button"
              class="mic-button"
              :class="{ recording: isRecording, busy: busy }"
              :disabled="busy || !recordSupported"
              @click="onMicClick"
              @pointerdown="onMicPointerDown"
              @pointerup="onMicPointerUp"
              @pointerleave="onMicPointerLeave"
              @pointercancel="onMicPointerUp"
              @contextmenu.prevent
            >
              <span class="mic-ring" :style="ringStyle"></span>
              <feather-icon :icon="isRecording ? 'SquareIcon' : 'MicIcon'" svgClasses="w-8 h-8" />
            </button>

            <div class="record-timer" :class="{ live: isRecording }">{{ isRecording ? elapsedText : maxText }}</div>

            <!-- 입력 레벨. 말하는데도 막대가 움직이지 않으면 장치가 잘못 잡힌 것이다. -->
            <div class="level-meter" :class="{ live: isRecording }">
              <div class="level-meter-fill" :style="{ width: Math.round(level * 100) + '%' }"></div>
            </div>

            <p class="record-hint">{{ hintText }}</p>

            <p v-if="inputWarning" class="input-warning">
              <feather-icon icon="AlertTriangleIcon" svgClasses="w-4 h-4" />
              <span class="ml-1">{{ inputWarning }}</span>
            </p>

            <!-- 마이크가 여러 개인 PC 에서 엉뚱한 장치가 잡히는 경우를 위해 직접 고를 수 있게 한다 -->
            <select v-if="devices.length > 1" v-model="deviceId" class="device-select" :disabled="isRecording || busy">
              <option value="">{{ $t("기본 마이크") }}</option>
              <option v-for="device in devices" :key="device.deviceId" :value="device.deviceId">
                {{ device.label || $t("마이크") }}
              </option>
            </select>

            <button v-if="recordSupported" type="button" class="mode-switch" :disabled="isRecording || busy" @click="switchMode">
              <feather-icon icon="RefreshCwIcon" svgClasses="w-3 h-3" />
              <span class="ml-1">{{ isHoldMode ? $t("클릭 방식으로 바꾸기") : $t("누르고 있기 방식으로 바꾸기") }}</span>
            </button>

            <p v-if="!recordSupported" class="empty-text danger">
              {{ $t("이 브라우저에서는 음성 녹음을 사용할 수 없습니다. 아래에 직접 입력해 주세요.") }}
            </p>
          </div>

          <div class="transcript-box">
            <label>{{ $t("인식된 내용 (수정 가능)") }}</label>
            <vs-textarea v-model="transcript" rows="3" :disabled="busy" />
            <vs-button size="small" color="primary" type="border" class="w-full" :disabled="busy || !transcript.trim()" @click="analyze">
              {{ $t("이 내용으로 분석") }}
            </vs-button>
          </div>
        </section>

        <!-- STEP 3 : 확인 후 적용 -->
        <section class="panel panel-result">
          <header class="panel-head">
            <span class="step-badge">3</span>
            <h6 class="panel-title">{{ $t("확인 후 적용") }}</h6>
            <span v-if="changes.length > 0" class="count-badge ml-auto">{{ selectedCount }}/{{ changes.length }}</span>
          </header>

          <div v-if="changes.length === 0" class="empty-block">
            <feather-icon icon="InboxIcon" svgClasses="w-6 h-6" />
            <p class="empty-text">{{ $t("아직 반영할 내용이 없습니다.") }}</p>
          </div>

          <ul v-else class="change-list">
            <li v-for="change in changes" :key="`change-${change.id}`" :class="{ selected: change.selected }" @click="change.selected = !change.selected">
              <vs-checkbox v-model="change.selected" @click.native.stop />
              <div class="change-body">
                <div class="change-label">
                  <em v-if="change.group">{{ $t(change.group) }}</em>{{ change.label }}
                </div>
                <div class="change-value">
                  <template v-if="change.overwrite">
                    <span class="before">{{ change.before }}</span>
                    <span class="arrow">→</span>
                  </template>
                  <strong>{{ change.after }}</strong>
                </div>
                <div v-if="change.note" class="change-note">{{ $t("비고") }}: {{ change.note }}</div>
              </div>
            </li>
          </ul>

          <div v-if="warnings.length > 0" class="warning-box">
            <div v-for="(warning, index) in warnings" :key="`warning-${index}`" class="warning-item">
              <feather-icon icon="AlertTriangleIcon" svgClasses="w-4 h-4" />
              <span>{{ warning }}</span>
            </div>
          </div>

        </section>
      </div>

      <div class="footer-row">
        <vs-button color="danger" type="flat" :disabled="busy || !hasContent" @click="resetAll">{{ $t("초기화") }}</vs-button>
        <span class="footer-spacer"></span>
        <vs-button color="secondary" type="border" @click="close">{{ $t("닫기") }}</vs-button>
        <vs-button color="secondary" type="border" :disabled="busy || changes.length === 0" @click="resetResult">{{ $t("다시 말하기") }}</vs-button>
        <vs-button color="primary" :disabled="busy || selectedCount === 0" @click="apply">{{ $t("폼에 적용") }} ({{ selectedCount }})</vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import { useVoiceRecorder, isRecordingSupported, listAudioInputs } from "@/composables/useVoiceRecorder";
import * as AiVoiceForm from "@/util/AiVoiceForm";

// 비용 보호 — 팝업 한 번에 허용하는 분석 횟수 / 한 번에 녹음할 수 있는 최대 길이
const MAX_ANALYZE_COUNT = 30;
const MAX_RECORD_MS = 60000;

// 이 값보다 입력 레벨이 낮으면 사람 목소리가 들어오지 않은 것으로 본다.
// (말소리는 보통 0.1~0.6, 조용한 사무실 잡음은 0.01 안팎)
const SILENCE_PEAK_THRESHOLD = 0.02;

/**
 * Whisper 가 무음을 받았을 때 내놓는 대표적인 환각 문장들.
 * 유튜브 자막으로 학습된 흔적이라, 소리가 없으면 거의 항상 이런 문장이 나온다.
 * 레벨 측정이 동작하지 않는 환경을 위한 2차 안전망이다.
 */
const HALLUCINATION_PATTERNS = [
  /시청해\s*주셔서\s*감사/,
  /구독과\s*좋아요/,
  /다음\s*영상에서\s*만나/,
  /thanks?\s*for\s*watching/i,
  /amara\.org/i,
];

function looksLikeSilenceHallucination(text) {
  return HALLUCINATION_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * 마우스가 있는 환경(PC)인지 판단한다.
 * PC 에서 버튼을 계속 누르고 있게 하는 건 불편하므로 클릭 시작/종료(토글)를 기본으로 하고,
 * 터치 기기에서는 무전기처럼 누르고 있는 동안 녹음하는 방식을 기본으로 한다.
 */
function isFinePointerDevice() {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

export default {
  name: "PopupAiVoiceWrite",
  props: {
    value: Boolean,
    boardId: [String, Number],
    schema: { type: Object, required: true },
    form: { type: Object, required: true },
  },
  data() {
    return {
      // 이 팝업은 버튼을 처음 누를 때 생성된다(v-if). 그 시점에 value 가 이미 true 이므로
      // false 로 시작하면 watch 가 돌지 않아 팝업이 열리지 않는다 — prop 으로 초기화한다.
      showPopup: this.value,
      windowWidth: window.innerWidth,

      recorder: null,
      recordSupported: isRecordingSupported(),
      recordMode: isFinePointerDevice() ? "toggle" : "hold",
      isRecording: false,
      level: 0,
      devices: [],
      deviceId: "",
      inputWarning: "",
      elapsedMs: 0,
      elapsedTimer: null,

      busy: false,
      busyText: "",
      transcript: "",
      changes: [],
      warnings: [],
      nextQuestions: [],
      analyzeCount: 0,
      unfilledLimit: 8,
    };
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    popupWidth() {
      return this.isMobile ? "95vw" : "1100px";
    },
    isHoldMode() {
      return this.recordMode === "hold";
    },
    progress() {
      return AiVoiceForm.computeProgress(this.schema, this.form);
    },
    visibleUnfilled() {
      return this.progress.unfilled.slice(0, this.unfilledLimit);
    },
    selectedCount() {
      return this.changes.filter((change) => change.selected).length;
    },
    hasContent() {
      return !!this.transcript.trim() || this.changes.length > 0 || this.warnings.length > 0 || this.nextQuestions.length > 0;
    },
    /**
     * 로그인한 작성자 정보. "점검자는 나야" 처럼 1인칭으로 말했을 때 AI 가 실제 이름으로
     * 채울 수 있도록 함께 보낸다. (서버가 JWT 기준으로 다시 채우므로 위조는 되지 않는다)
     */
    speaker() {
      const userInfo = this.$store.state.auth.userInfo || {};
      const companyInfo = this.$store.state.auth.companyInfo || {};
      return {
        name: userInfo.name || "",
        company: companyInfo.com_name || "",
        department: userInfo.department || "",
        section: userInfo.section || "",
        rank: userInfo.rank || userInfo.position || "",
      };
    },
    statusText() {
      if (this.busy) return this.busyText;
      if (this.isRecording) return this.$t("듣고 있습니다");
      if (!this.recordSupported) return this.$t("녹음을 사용할 수 없습니다");
      return this.isHoldMode ? this.$t("버튼을 누른 채 말씀하세요") : this.$t("버튼을 눌러 시작하세요");
    },
    hintText() {
      if (this.isHoldMode) return this.$t("누르고 있는 동안 녹음되고, 손을 떼면 분석합니다.");
      if (this.isRecording) return this.$t("다 말씀하셨으면 버튼을 다시 눌러 종료합니다.");
      return this.$t("버튼을 누르면 녹음이 시작되고, 다시 누르면 분석합니다.");
    },
    elapsedText() {
      return this.formatTime(this.elapsedMs);
    },
    maxText() {
      return this.$t("최대") + " " + this.formatTime(MAX_RECORD_MS);
    },
    ringStyle() {
      return { transform: `scale(${1 + this.level * 0.45})`, opacity: this.isRecording ? 0.35 + this.level * 0.5 : 0 };
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(active) {
        // 팝업을 닫아도 작성 중이던 내용은 지우지 않는다. 폼을 확인하러 잠깐 닫는
        // 경우가 많아서다. 비우려면 사용자가 "초기화"를 눌러야 한다.
        this.showPopup = active;
      },
    },
    showPopup(active) {
      this.$emit("input", active);
      if (!active) this.stopRecordingSilently();
    },
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.loadDevices();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    this.stopRecordingSilently();
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth;
    },

    formatTime(ms) {
      const total = Math.floor(ms / 1000);
      const mm = `${Math.floor(total / 60)}`.padStart(2, "0");
      const ss = `${total % 60}`.padStart(2, "0");
      return `${mm}:${ss}`;
    },

    // 사용자가 명시적으로 누를 때만 전부 비운다.
    resetAll() {
      if (this.hasContent && !confirm(this.$t("작성 중인 내용을 모두 지웁니다. 계속할까요?"))) return;

      this.transcript = "";
      this.changes = [];
      this.warnings = [];
      this.nextQuestions = [];
      this.analyzeCount = 0;
      this.level = 0;
      this.elapsedMs = 0;
    },

    // 인식 결과만 비운다(다시 말하기).
    resetResult() {
      this.transcript = "";
      this.changes = [];
      this.warnings = [];
    },

    close() {
      this.showPopup = false;
    },

    switchMode() {
      this.recordMode = this.isHoldMode ? "toggle" : "hold";
    },

    async loadDevices() {
      if (!this.recordSupported) return;
      this.devices = await listAudioInputs();
    },

    _createRecorder() {
      return useVoiceRecorder({
        maxMs: MAX_RECORD_MS,
        deviceId: this.deviceId || undefined,
        onLevel: (level) => {
          this.level = level;
        },
        onAutoStop: () => {
          // 최대 녹음 시간 초과 — 지금까지 녹음한 내용으로 진행한다.
          this.finishRecording();
        },
      });
    },

    // ── 녹음 ──────────────────────────────────────────────
    async startRecording() {
      if (this.isRecording || this.busy || !this.recordSupported) return;
      try {
        this.recorder = this._createRecorder();
        await this.recorder.start();
        this.isRecording = true;
        this.elapsedMs = 0;
        this.elapsedTimer = setInterval(() => {
          this.elapsedMs += 200;
        }, 200);
      } catch (e) {
        this.recorder = null;
        this.isRecording = false;
        this.alert(this.$t("마이크를 사용할 수 없습니다. 브라우저 권한을 확인해 주세요."));
      }
    },

    async finishRecording() {
      if (!this.isRecording || !this.recorder) return;
      this.isRecording = false;
      this._clearTimer();

      const result = await this.recorder.stop();
      this.recorder = null;
      // 권한을 허용한 뒤에야 장치 이름을 읽을 수 있어 여기서 목록을 갱신한다.
      this.loadDevices();
      if (!result) return; // 너무 짧은 녹음은 무시

      // 무음을 그대로 Whisper 에 보내면 "시청해주셔서 감사합니다" 같은 환각 문장이
      // 돌아온다. 보내기 전에 걸러내고 원인을 알려준다.
      if (result.levelAvailable && result.peakLevel < SILENCE_PEAK_THRESHOLD) {
        this.inputWarning = this.$t("마이크에서 소리가 감지되지 않았습니다. 입력 장치와 음소거 상태를 확인해 주세요.");
        this.alert(this.inputWarning);
        return;
      }

      this.inputWarning = "";
      await this.transcribe(result);
    },

    stopRecordingSilently() {
      if (this.recorder) {
        this.recorder.cancel();
        this.recorder = null;
      }
      this.isRecording = false;
      this.level = 0;
      this._clearTimer();
    },

    _clearTimer() {
      if (this.elapsedTimer) {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
      }
    },

    // 클릭(토글) 방식 — PC 기본
    onMicClick() {
      if (this.isHoldMode) return; // 누르고 있기 방식에서는 pointer 이벤트가 처리한다
      if (this.isRecording) this.finishRecording();
      else this.startRecording();
    },

    // 누르고 있기 방식 — 터치 기기 기본
    onMicPointerDown() {
      if (!this.isHoldMode) return;
      this.startRecording();
    },
    onMicPointerUp() {
      if (!this.isHoldMode) return;
      this.finishRecording();
    },
    onMicPointerLeave() {
      // 버튼 밖으로 손가락이 나가면 녹음이 끊긴 줄 모르게 되므로 마무리한다.
      if (!this.isHoldMode || !this.isRecording) return;
      this.finishRecording();
    },

    // ── STT → 분석 ────────────────────────────────────────
    async transcribe(record) {
      this.busy = true;
      this.busyText = this.$t("음성을 인식하는 중입니다...");
      try {
        const response = await this.$store.dispatch("miras/VOICE_TRANSCRIBE", {
          audio: record.blob,
          filename: record.filename,
          language: "ko",
        });
        const text = (response.transcribed_text || "").trim();
        if (!text) {
          this.alert(this.$t("인식된 내용이 없습니다. 다시 말씀해 주세요."));
          return;
        }
        // 레벨 측정이 막힌 환경(오디오 컨텍스트 차단 등)을 위한 2차 안전망.
        if (looksLikeSilenceHallucination(text)) {
          this.inputWarning = this.$t("마이크에서 소리가 감지되지 않았습니다. 입력 장치와 음소거 상태를 확인해 주세요.");
          this.alert(this.inputWarning);
          return;
        }
        this.transcript = text;
      } catch (e) {
        this.alert(this.$t("음성 인식에 실패했습니다."));
        return;
      } finally {
        this.busy = false;
      }

      await this.analyze();
    },

    async analyze() {
      const transcript = (this.transcript || "").trim();
      if (!transcript || this.busy) return;

      if (this.analyzeCount >= MAX_ANALYZE_COUNT) {
        this.alert(this.$t("분석 요청이 너무 많습니다. 초기화 후 다시 시도해 주세요."));
        return;
      }

      this.busy = true;
      this.busyText = this.$t("AI가 내용을 분석하는 중입니다...");
      try {
        this.analyzeCount += 1;
        const data = await this.$store.dispatch("miras/FORM_FILL_ANALYZE", {
          boardId: this.boardId,
          schema: AiVoiceForm.toLlmSchema(this.schema),
          formState: AiVoiceForm.toFormState(this.schema, this.form),
          transcript,
          locale: this.$i18n.locale,
          speaker: this.speaker,
        });

        // 발화에 없더라도 작성자 정보로 채울 수 있는 항목(소속명·점검자 등)을 함께 제안한다.
        // 발화로 받은 값이 항상 우선이다.
        const patch = Object.assign(
          {},
          AiVoiceForm.speakerPatch(this.schema, this.form, this.speaker, Object.keys(data.patch || {})),
          data.patch || {}
        );

        const described = AiVoiceForm.describePatch(this.schema, this.form, patch);
        // 새 발화 결과를 기존 변경 목록에 합친다(같은 항목은 최신 값으로 교체).
        const merged = this.changes.filter((change) => !described.changes.some((item) => item.id === change.id));
        this.changes = merged.concat(described.changes);

        this.warnings = (data.warnings || []).slice();
        described.rejected.forEach((item) => {
          this.warnings.push(this.$t("적용하지 않음") + `: ${item.id} (${item.reason})`);
        });
        this.nextQuestions = data.next_questions || [];

        if (described.changes.length === 0 && this.warnings.length === 0) {
          this.warnings.push(this.$t("말씀하신 내용에서 채울 항목을 찾지 못했습니다."));
        }
      } catch (e) {
        this.alert(e.message || this.$t("음성 분석에 실패했습니다."));
      } finally {
        this.busy = false;
      }
    },

    // ── 적용 ──────────────────────────────────────────────
    apply() {
      const selected = this.changes.filter((change) => change.selected);
      if (selected.length === 0) return;

      const next = AiVoiceForm.applyChanges(this.schema, this.form, selected);
      this.$emit("apply", next);

      this.changes = [];
      this.transcript = "";
      this.warnings = [];
    },

    alert(message) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: this.$t("AI 음성 작성"),
          messageIcon: "AlertCircleIcon",
          message,
          size: "small",
          btnPosition: "center",
        },
      });
      window.dispatchEvent(customAlert);
    },
  },
};
</script>

<style lang="scss" scoped>
// vs-popup 의 width prop 은 이 프로젝트에서 신뢰할 수 없어(다른 팝업들도 동일)
// 폭/높이는 여기서 직접 잡는다. 안 잡으면 3열이 눌려 내부 스크롤이 생기고
// 하단 버튼이 잘린다.
#popupAiVoiceWrite::v-deep {
  .vs-popup {
    width: 1180px;
    max-width: 96vw;
  }

  .vs-popup--content {
    // 팝업 자체는 스크롤하지 않고, 내용이 긴 패널만 각자 스크롤한다.
    max-height: 88vh;
    overflow-y: auto;
    padding: 1rem 1.25rem 1.25rem !important;
  }
}

@media (max-width: 1199px) {
  #popupAiVoiceWrite::v-deep .vs-popup {
    width: 720px;
  }
}

@media (max-width: 767px) {
  #popupAiVoiceWrite::v-deep {
    .vs-popup {
      width: 96vw;
    }
    .vs-popup--content {
      max-height: 86vh;
      padding: 0.75rem !important;
    }
  }
}

$primary: #7367f0;
$danger: #ea5455;
$success: #28c76f;
$line: #e8e8ee;
$muted: #8a8a9e;

.ai-voice {
  // ── 진행률 ───────────────────────────────────────────
  .progress-row {
    margin-bottom: 1.1rem;
  }

  .progress-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 0.4rem;
  }

  .progress-percent {
    font-size: 1.35rem;
    font-weight: 700;
    color: $primary;
  }

  .progress-count {
    margin-left: 0.5rem;
    font-size: 0.85rem;
    color: $muted;
  }

  .progress-done {
    display: inline-flex;
    align-items: center;
    font-size: 0.85rem;
    color: $success;
  }

  .progress-bar-bg {
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background: #eeeef4;
    overflow: hidden;
  }

  .progress-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, $primary, $success);
    transition: width 0.35s ease;
  }

  // ── 3단계 레이아웃 ────────────────────────────────────
  .body-grid {
    display: grid;
    grid-template-columns: 1fr 340px 1.1fr;
    gap: 0.9rem;
    align-items: stretch;

    // 태블릿: 녹음 패널을 위로 올리고 안내/결과를 아래 2열로
    @media (max-width: 1199px) {
      grid-template-columns: 1fr 1fr;

      .panel-record {
        grid-column: 1 / -1;
        order: -1;
      }
    }

    // 모바일: 한 열, 녹음이 맨 위
    @media (max-width: 767px) {
      grid-template-columns: 1fr;
    }
  }

  .panel {
    position: relative;
    display: flex;
    flex-direction: column;
    border: 1px solid $line;
    border-radius: 10px;
    padding: 0.9rem 0.95rem 1rem;
    background: #fcfcfe;
    // 내용이 길어져도 팝업 전체가 밀리지 않도록 패널별로 스크롤한다.
    max-height: 58vh;
    overflow-y: auto;

    @media (max-width: 767px) {
      max-height: none;
    }
  }

  // 단계별로 색을 달리해 한눈에 구분되게 한다.
  .panel-guide {
    .step-badge {
      background: #a9a2f5;
    }
  }
  .panel-record {
    background: #fff;
    border-color: #d9d5fb;
    box-shadow: 0 2px 10px rgba(115, 103, 240, 0.08);
    max-height: none;
    overflow: visible;

    .step-badge {
      background: $primary;
    }

    &.is-recording {
      border-color: rgba($danger, 0.55);
      box-shadow: 0 2px 14px rgba($danger, 0.16);
    }
  }
  .panel-result {
    .step-badge {
      background: #52c8a0;
    }
  }

  .panel-head {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding-bottom: 0.55rem;
    margin-bottom: 0.7rem;
    border-bottom: 1px solid $line;
  }

  .step-badge {
    flex: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: #fff;
    font-size: 0.72rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .panel-title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
  }

  .panel-subhead {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    margin: 0.9rem 0 0.45rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #555;
  }

  .count-badge {
    display: inline-block;
    min-width: 20px;
    padding: 0 0.35rem;
    border-radius: 10px;
    background: #eeeef4;
    color: #666;
    font-size: 0.72rem;
    font-weight: 600;
    text-align: center;
    line-height: 1.35rem;
  }

  // ── STEP 1 ───────────────────────────────────────────
  .next-question-box {
    margin-bottom: 0.8rem;
    padding: 0.55rem 0.65rem;
    border: 1px solid #cfe6f7;
    border-radius: 8px;
    background: #f2f9fe;
  }

  .next-question-head {
    display: flex;
    align-items: center;
    margin-bottom: 0.3rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: #2b7bb9;
  }

  .example-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      position: relative;
      margin-bottom: 0.4rem;
      padding: 0.45rem 0.6rem 0.45rem 0.7rem;
      border-left: 3px solid #cfcaf8;
      border-radius: 0 6px 6px 0;
      background: #f5f4fd;
      font-size: 0.83rem;
      line-height: 1.45;
      color: #4b4b63;
    }
  }

  .chip-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 0.2rem 0.5rem;
    border: 1px solid $line;
    border-radius: 12px;
    background: #fff;
    font-size: 0.76rem;
    color: #555;

    em {
      margin-right: 0.3rem;
      padding: 0 0.25rem;
      border-radius: 3px;
      background: #f0eefc;
      color: #7a74a8;
      font-style: normal;
      font-size: 0.68rem;
    }
  }

  .chip-more {
    color: $muted;
  }

  // ── STEP 2 ───────────────────────────────────────────
  .record-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .record-status {
    display: inline-flex;
    align-items: center;
    min-height: 1.4rem;
    margin-bottom: 0.8rem;
    font-size: 0.88rem;
    font-weight: 600;
    color: #555;

    &.live {
      color: $danger;
    }
    &.busy {
      color: $primary;
    }
  }

  .rec-dot {
    width: 8px;
    height: 8px;
    margin-right: 0.4rem;
    border-radius: 50%;
    background: $danger;
    animation: recBlink 1s infinite;
  }

  @keyframes recBlink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.25;
    }
  }

  .mic-button {
    position: relative;
    width: 92px;
    height: 92px;
    border-radius: 50%;
    border: none;
    background: $primary;
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    touch-action: none;
    user-select: none;
    transition: background 0.15s ease, transform 0.1s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
    }

    &.recording {
      background: $danger;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  .mic-ring {
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    border: 3px solid rgba($danger, 0.5);
    opacity: 0;
    transition: transform 0.1s linear, opacity 0.15s linear;
  }

  .record-timer {
    margin-top: 0.55rem;
    font-size: 0.8rem;
    font-variant-numeric: tabular-nums;
    color: $muted;

    &.live {
      color: $danger;
      font-weight: 600;
    }
  }

  .level-meter {
    width: 140px;
    height: 5px;
    margin-top: 0.45rem;
    border-radius: 3px;
    background: #eeeef4;
    overflow: hidden;
  }

  .level-meter-fill {
    height: 100%;
    width: 0;
    background: #c9c6e8;
    transition: width 0.1s linear;
  }

  .level-meter.live .level-meter-fill {
    background: $danger;
  }

  .record-hint {
    margin-top: 0.3rem;
    font-size: 0.78rem;
    line-height: 1.45;
    color: $muted;
  }

  .input-warning {
    margin-top: 0.5rem;
    padding: 0.4rem 0.5rem;
    border-radius: 6px;
    background: #fff7e8;
    color: #9a6200;
    font-size: 0.76rem;
    line-height: 1.4;
    text-align: left;
  }

  .device-select {
    width: 100%;
    margin-top: 0.5rem;
    padding: 0.3rem 0.4rem;
    border: 1px solid $line;
    border-radius: 6px;
    background: #fff;
    font-size: 0.76rem;
    color: #555;
  }

  .mode-switch {
    display: inline-flex;
    align-items: center;
    margin-top: 0.5rem;
    padding: 0.2rem 0.5rem;
    border: 1px dashed #d5d5e0;
    border-radius: 12px;
    background: transparent;
    color: #6c6c85;
    font-size: 0.74rem;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: $primary;
      color: $primary;
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .transcript-box {
    width: 100%;
    margin-top: 1rem;
    padding-top: 0.85rem;
    border-top: 1px dashed $line;
    text-align: left;

    label {
      display: block;
      margin-bottom: 0.3rem;
      font-size: 0.8rem;
      color: #666;
    }
  }

  // ── STEP 3 ───────────────────────────────────────────
  .empty-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 120px;
    color: #c3c3d2;
  }

  .empty-text {
    margin: 0.4rem 0 0;
    font-size: 0.83rem;
    color: $muted;

    &.success {
      color: $success;
    }
    &.danger {
      color: $danger;
    }
  }

  .change-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      display: flex;
      gap: 0.5rem;
      align-items: flex-start;
      margin-bottom: 0.4rem;
      padding: 0.5rem 0.6rem;
      border: 1px solid $line;
      border-radius: 8px;
      background: #fff;
      cursor: pointer;

      &.selected {
        border-color: rgba($success, 0.5);
        background: #f4fcf8;
      }
    }
  }

  .change-body {
    min-width: 0;
    flex: 1;
  }

  .change-label {
    font-size: 0.82rem;
    color: #444;
    line-height: 1.35;

    em {
      margin-right: 0.3rem;
      padding: 0 0.25rem;
      border-radius: 3px;
      background: #f0eefc;
      color: #7a74a8;
      font-style: normal;
      font-size: 0.68rem;
    }
  }

  .change-value {
    margin-top: 0.15rem;
    font-size: 0.9rem;

    .before {
      color: #b0b0c0;
      text-decoration: line-through;
    }
    .arrow {
      margin: 0 0.3rem;
      color: $muted;
    }
    strong {
      color: #2f9e6d;
    }
  }

  .change-note {
    margin-top: 0.1rem;
    font-size: 0.76rem;
    color: #777;
  }

  .warning-box {
    margin-top: 0.7rem;
    padding: 0.5rem 0.6rem;
    border-radius: 8px;
    background: #fff7e8;
    color: #9a6200;
    font-size: 0.78rem;
  }

  .warning-item {
    display: flex;
    align-items: flex-start;
    gap: 0.3rem;
    line-height: 1.4;

    & + .warning-item {
      margin-top: 0.25rem;
    }
  }

  .question-list {
    margin: 0;
    padding-left: 1.1rem;
    font-size: 0.82rem;
    color: #4b4b63;

    li {
      margin-bottom: 0.25rem;
      line-height: 1.45;
    }
  }

  // 내용이 길어도 항상 눌릴 수 있게 아래에 고정한다.
  .footer-row {
    position: sticky;
    bottom: 0;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 0.75rem;
    background: #fff;
    border-top: 1px solid $line;
  }

  .footer-spacer {
    flex: 1;
  }
}
</style>
